<template>
	<div class="element">
		<div class="row">
			<div class="col-lg-12">
				<h4>{{ $t('customers.seReports.headline') }}</h4>

				<div class="reports">
					<div class="headline">
						<div class="subject">
							{{ $t('customers.seReports.columns.reportName') }}
						</div>

						<div class="date">
							{{ $t('customers.seReports.columns.date') }}
						</div>

						<div class="actions">
							{{ $t('customers.seReports.columns.actions') }}
						</div>
					</div>
				</div>

				<div class="reports">
					<div v-for="(report, index) in reports"
						 :key="`report-${index}`"
						 class="report">
						<div class="subject">
							<div class="labels">
								<div class="name">{{ report.subject }}</div>
							</div>
						</div>

						<div class="date">
							{{ report.sent_at }}
						</div>

						<div class="actions">
							<div class="action-buttons">
								<a href="#"
								   @click.prevent="viewReport(report)"
								   class="view-button">
									<i class="fa fa-eye" />
									<span>{{ $t('customers.seReports.actions.view') }}</span>
								</a>
								<a href="#"
								   @click.prevent="downloadReport(report.html_link)"
								   class="download-button">
									<i class="fa fa-download" />
									<span>{{ $t('customers.seReports.actions.download') }}</span>
								</a>
							</div>
						</div>
					</div>

					<logo-loader v-if="! hasLoaded"
								 :height="355"
					/>

					<div v-if="hasLoaded && reports.length === 0"
						 class="no-reports">
						{{ $t('customers.seReports.noReports') }}
					</div>

					<!-- <div v-if="errorMessage" class="error-message">
						{{ $t('customers.seReports.modal.error', { error: errorMessage }) }}
					</div> -->
				</div>
			</div>
		</div>

		<report-viewer-modal
			v-if="showReportModal"
			:customer-id="customerId"
			:report-id="selectedReportId"
			:report-name="selectedReportName"
			@close="showReportModal = false"
		/>
	</div>
</template>

<script>
import { mapGetters } from 'vuex'
import LogoLoader from "@/app/shared/components/LogoLoader"
import { getReports, downloadReport } from '@/services/se-reports/SeReportsService'
import ReportViewerModal from './se-reports/components/ReportViewerModal'

export default {
	data() {
		return {
			hasLoaded: false,
			reports: [],
			isLoading: false,
			errorMessage: '',
			showReportModal: false,
			selectedReportId: '',
			selectedReportName: ''
		}
	},

	computed: {
		...mapGetters("identity", {
			identity: "getIdentity"
		}),

		customerId() {
			return this.$route.params.id
		}
	},

	mounted() {
		this.loadReports()
	},

	methods: {
		loadReports() {
			this.hasLoaded = false
			this.isLoading = true
			this.errorMessage = ''

			getReports(
				this.customerId,
				response => {
					this.reports = response.data.data || []
					this.hasLoaded = true
					this.isLoading = false
				},
				error => {
					this.hasLoaded = true
					this.isLoading = false
					this.errorMessage = (error.response?.data?.message || error.message || '')
				}
			)
		},

		downloadReport(htmlLink) {
			const reportIdMatch = htmlLink.match(/report_([\w-]+)_/)

			if (!reportIdMatch || !reportIdMatch[1]) {
				return
			}

			const reportId = reportIdMatch[1]

			downloadReport(
				this.customerId,
				reportId,
				'html',
				response => {
					// Create a blob URL and trigger download
					const blob = new Blob([response.data], { type: 'text/html' })
					const link = document.createElement('a')
					link.href = window.URL.createObjectURL(blob)
					link.download = `report-${reportId}.html`
					document.body.appendChild(link)
					link.click()
					document.body.removeChild(link)
				},
				error => {
					alert(this.$t('customers.seReports.modal.error', { error: (error.response?.data?.message || error.message || 'Unknown error') }))
				}
			)
		},

		viewReport(report) {
			const reportIdMatch = report.html_link.match(/report_([\w-]+)_/)

			if (!reportIdMatch || !reportIdMatch[1]) {
				return
			}

			this.selectedReportId = reportIdMatch[1]
			this.selectedReportName = report.subject
			this.showReportModal = true
		},
	},

	components: {
		LogoLoader,
		ReportViewerModal
	}
}
</script>

<style lang="scss" scoped>
.reports {
	margin-bottom: 30px;
	border-radius: 4px;
	background: #fff;

	.headline {
		display: flex;
		padding: 15px;
		background: #f3f3f3;
		border-top-left-radius: 4px;
		border-top-right-radius: 4px;
		border-bottom: 1px solid #ddd;
		font-weight: 600;
		text-transform: uppercase;
		font-size: 11px;
		letter-spacing: 1px;
		color: #666;

		.subject {
			flex-grow: 1;
		}

		.date {
			width: 180px;
			text-align: center;
		}

		.actions {
			width: 180px;
			text-align: right;
		}
	}

	.report {
		display: flex;
		padding: 20px 15px;
		position: relative;
		border-bottom: 1px solid #eee;

		&:last-child {
			border-bottom: 0;
		}

		.subject {
			flex-grow: 1;

			.labels {
				.name {
					font-weight: 500;
					font-size: 12pt;
				}
			}
		}

		.date {
			width: 180px;
			text-align: center;
			padding-top: 3px;
		}

		.actions {
			width: 180px;
			text-align: right;

			.action-buttons {
				display: flex;
				gap: 8px;
				justify-content: flex-end;
			}

			.download-button,
			.view-button {
				display: inline-block;
				padding: 5px 10px;
				border-radius: 3px;
				font-size: 11px;
				cursor: pointer;
				text-decoration: none;

				i {
					margin-right: 5px;
				}
			}

			.download-button {
				background-color: #2196f3;
				color: white;

				&:hover {
					background-color: darken(#2196f3, 10%);
				}
			}

			.view-button {
				background-color: #fff;
				color: #2196f3;
				border: 1px solid #2196f3;

				&:hover {
					background-color: #e6f7ff;
				}
			}
		}
	}

	.no-reports {
		padding: 50px 20px;
		text-align: center;
		color: #999;
	}

	.error-message {
		padding: 15px;
		text-align: center;
		color: #721c24;
		background-color: #f8d7da;
		border: 1px solid #f5c6cb;
		border-radius: 4px;
		margin-top: 15px;
	}
}
</style>
